import { z } from 'zod';
import { HttpUrl } from './common/url.js';
import { Taxonomy } from './taxonomy.js';
import { EngagementCounts } from './engagement.js';
/**
 * Content discriminator. All six types live in one `content` collection with a
 * Mongoose discriminator, because the feed reads them together and a six-way
 * $unionWith would be unindexable (docs/06 ADR-04).
 *
 * NOTE: `reel` and the video attachment kind are declared here but are not
 * wired in this scaffold — video upload, media editing and live video are
 * currently deferred. See README "Deferred scope".
 */
export const ContentType = z.enum(['post', 'reel', 'poll', 'fundraiser', 'event', 'debate']);
/**
 * What a member can actually author today. `reel` and `debate` are declared in
 * `ContentType` but have no create endpoint — video and debates are deferred
 * (README "Deferred scope") — so the composer offers these four only.
 */
export const CreatableContentType = z.enum(['post', 'poll', 'fundraiser', 'event']);
/** Display labels for the Create menu and the composer's type switcher. */
export const CONTENT_TYPE_LABELS = {
    post: 'Post',
    poll: 'Poll',
    fundraiser: 'Fundraiser',
    event: 'Event',
};
/** Who can see a post (FR-POST-03). */
export const Audience = z.enum(['anyone', 'community', 'connections']);
export const ContentStatus = z.enum(['draft', 'scheduled', 'published', 'hidden', 'deleted']);
export const AttachmentKind = z.enum(['image', 'video']);
export const Attachment = z.object({
    kind: AttachmentKind,
    url: z.string().url(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
/**
 * The same attachment as a member SENDS it, with the scheme allowlist applied.
 *
 * Split from `Attachment` rather than tightening it, because this schema is
 * also the response shape: `HttpUrl` there would make rows already stored with
 * a non-conforming url fail on READ, which is the one outcome the allowlist
 * work set out to avoid. Requests are validated strictly, responses stay
 * permissive — the same input/output asymmetry the profile and community
 * fields use.
 *
 * `.extend` rather than a restatement, so a field added to `Attachment` is
 * carried here without anyone remembering to.
 */
export const AttachmentInput = Attachment.extend({ url: HttpUrl });
export const GeoLocation = z.object({
    label: z.string().min(1).max(200),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
/** Fields shared by every content type. */
export const ContentBase = Taxonomy.extend({
    subcategory: z.literal('travel').optional(),
    tripId: z.string().optional(),
    bucketItemId: z.string().optional(),
    id: z.string(),
    type: ContentType,
    authorId: z.string(),
    communityId: z.string().min(1).optional(),
    /** Current group visibility and viewer permissions, resolved on every read. */
    community: z
        .object({
        id: z.string(),
        name: z.string(),
        visibility: z.enum(['public', 'private']),
        canInteract: z.boolean(),
        canManage: z.boolean(),
    })
        .optional(),
    title: z.string().min(1).max(120),
    body: z.string().max(5_000).optional(),
    audience: Audience.default('anyone'),
    commentsEnabled: z.boolean().default(true),
    opvEnabled: z.boolean().default(true),
    location: GeoLocation.optional(),
    hashtags: z
        .array(z.string().regex(/^[a-z0-9_]+$/i))
        .max(30)
        .default([]),
    attachments: z.array(Attachment).default([]),
    groupReviewStatus: z.enum(['pending', 'rejected']).optional(),
    status: ContentStatus,
    scheduledAt: z.coerce.date().optional(),
    publishedAt: z.coerce.date().optional(),
    engagement: EngagementCounts,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    /**
     * Event summary fields are optional on the shared feed shape. They are
     * present when `type === 'event'`, allowing universal post lists to render
     * the event card without making a second request per row.
     */
    venueName: z.string().optional(),
    address: z.string().optional(),
    startsAt: z.coerce.date().optional(),
    endsAt: z.coerce.date().optional(),
    timezone: z.string().optional(),
    admissionMode: z.enum(['open_free', 'rsvp_free', 'ticketed']).optional(),
    ticketAllocation: z.number().int().optional(),
    ticketsBooked: z.number().int().optional(),
    /** Viewer-relative attendance, when the endpoint has viewer context. */
    myTickets: z.number().int().nonnegative().optional(),
    ticketPriceMinor: z.number().int().optional(),
    currency: z.string().length(3).optional(),
    interestedCount: z.number().int().nonnegative().optional(),
    isInterested: z.boolean().optional(),
    isGoing: z.boolean().optional(),
});
/** Create payload. Both taxonomies required, no defaults — AC-GL-17. */
export const CreatePostInput = Taxonomy.extend({
    subcategory: z.literal('travel').optional(),
    communityId: z.string().min(1).optional(),
    title: z.string().min(1).max(120),
    body: z.string().max(5_000).optional(),
    audience: Audience.default('anyone'),
    commentsEnabled: z.boolean().default(true),
    opvEnabled: z.boolean().default(true),
    location: GeoLocation.optional(),
    hashtags: z.array(z.string()).max(30).default([]),
    attachments: z.array(AttachmentInput).max(10).default([]),
    scheduledAt: z.coerce.date().optional(),
});
/** HTML screen 26 offers these fixed poll lifetimes. */
export const PollDuration = z.enum(['1d', '3d', '7d']);
/** Poll options: 2 minimum, 6 maximum, enforced at both ends. */
export const CreatePollInput = Taxonomy.extend({
    title: z.string().min(1).max(120),
    body: z.string().max(5_000).optional(),
    options: z.array(z.string().trim().min(1).max(80)).min(2).max(6),
    duration: PollDuration.default('3d'),
    commentsEnabled: z.boolean().default(true),
    opvEnabled: z.boolean().default(true),
    hashtags: z.array(z.string()).max(30).default([]),
    attachments: z.array(AttachmentInput).max(1).default([]),
    scheduledAt: z.coerce.date().optional(),
});
//# sourceMappingURL=content.js.map